import { cdnUrl } from "commons/cdn";
import { MyImage } from "components/MyImage";
import { MyMedia } from "components/MyMedia";
import { type FC, type ReactNode } from "react";
import DisplayUpdateTime from "ui/DisplayUpdateTime";

export type AvatarCardProps = {
  avatar: any;
  /** 右上角操作区，例如移除套装按钮 */
  actionsSlot?: ReactNode;
};

export const AvatarCard: FC<AvatarCardProps> = ({ avatar, actionsSlot }) => {
  const thumbSrc = avatar?.thumb ?? avatar?.url;
  const previewSrc = avatar?.url ?? avatar?.thumb;
  const showSpineVideo =
    avatar?.engineWith === "spine" &&
    !!(avatar?.videoPreviewUrl || avatar?.videoUrl);

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="h-36 w-36 shrink-0 overflow-hidden rounded-md border bg-background">
            {showSpineVideo ? (
              <MyMedia
                alt={avatar?.id ?? "avatar"}
                videoUrl={avatar.videoPreviewUrl || avatar.videoUrl}
                previewVideoUrl={avatar.videoUrl || avatar.videoPreviewUrl}
                imageUrl={thumbSrc}
                lazyVideo
                className="h-full w-full"
              />
            ) : thumbSrc ? (
              <MyImage
                src={cdnUrl(thumbSrc)}
                previewSrc={previewSrc ? cdnUrl(previewSrc) : undefined}
                alt="avatar"
                className="h-full w-full object-contain"
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1 space-y-2 text-xs">
            <div className="font-mono break-all text-sm text-foreground">
              {avatar.id}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-start text-muted-foreground">
              <div>
                tid ·{" "}
                <span className="font-mono text-foreground">
                  {avatar.tid ?? "—"}
                </span>
              </div>
              <div>
                clientVer ·{" "}
                <span className="font-mono text-foreground">
                  {avatar.clientVer != null ? String(avatar.clientVer) : "—"}
                </span>
              </div>
              <div className="min-w-0">
                {avatar.authorName != null && avatar.authorName !== "" ? (
                  <div>
                    author ·{" "}
                    <span className="truncate font-mono text-foreground">
                      {avatar.authorName}
                    </span>
                  </div>
                ) : avatar.authorId != null ? (
                  <div>
                    authorId ·{" "}
                    <span className="truncate font-mono text-foreground">
                      {avatar.authorId}
                    </span>
                  </div>
                ) : (
                  <>author · —</>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                {avatar.layerCount != null ? (
                  <div>
                    layerCount ·{" "}
                    <span className="truncate font-mono text-foreground">
                      {avatar.layerCount}
                    </span>
                  </div>
                ) : (
                  <div>
                    layerCount ·{" "}
                    <span className="truncate font-mono text-foreground">
                      —
                    </span>
                  </div>
                )}
                {avatar.cptCount != null ? (
                  <div>
                    cptCount ·{" "}
                    <span className="truncate font-mono text-foreground">
                      {avatar.cptCount}
                    </span>
                  </div>
                ) : (
                  <div>
                    cptCount ·{" "}
                    <span className="truncate font-mono text-foreground">
                      —
                    </span>
                  </div>
                )}
              </div>
              <div>
                state ·{" "}
                <span className="text-foreground">
                  {avatar.state != null ? avatar.state : "—"}
                </span>
              </div>
              <div className="min-w-0">
                updateTime ·{" "}
                <span className="truncate font-mono text-foreground">
                  {avatar.updateTime != null ? (
                    <DisplayUpdateTime updateTime={avatar.updateTime} />
                  ) : (
                    <span className="text-foreground">—</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        {actionsSlot != null && (
          <div className="shrink-0 pt-0.5">{actionsSlot}</div>
        )}
      </div>
    </div>
  );
};
